import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { DropDownMenu } from "../../atoms/DropDownMenu";
import { DropDownItem } from "../../atoms/DropDownItem";
import { useScrollLock } from "../../../hooks/useScrollLock";
import useViewportChangeTick from "../../../hooks/useViewportChangeTick";
import classNames from "../../../utils/classNames";
import calculateFloatingPosition from "../../../utils/floatingPosition";
import AnchoredPortalContext from "../AnchoredPortal/AnchoredPortalContext";
import "./index.css";

const defaultGetItemId = (item, index) => item?.id ?? `dropdown-item-${index}`;
const defaultGetItemLabel = (item) => item?.label ?? "";
const DEFAULT_OFFSET = { x: 0, y: 6 };
const DEFAULT_PORTAL_ROOT_ID = "portal";
const elementRefPropType = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
]);

const resolvePortalRoot = (portalRootId = DEFAULT_PORTAL_ROOT_ID) => {
  if (typeof document === "undefined") {
    return null;
  }

  if (portalRootId) {
    const portalRoot = document.getElementById(portalRootId);

    if (portalRoot) {
      return portalRoot;
    }
  }

  return document.body;
};

const resolveRefElement = (ref) => {
  if (!ref || typeof ref === "function") {
    return null;
  }

  return ref.current ?? null;
};

const assignRef = (ref, node) => {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(node);
    return;
  }

  ref.current = node;
};

const DropDown = ({
  dropdownRef,
  anchorRef,
  items,
  renderItem,
  onItemSelect,
  getItemId = defaultGetItemId,
  getItemLabel = defaultGetItemLabel,
  menuRole = "menu",
  itemClassName = "",
  noItemsMessage = "No items found.",
  className = "",
  lockScroll = false,
  placement = "bottom-start",
  offset = DEFAULT_OFFSET,
  gap = 8,
  flip = true,
  strategy = "absolute",
  matchTriggerWidth = false,
  renderInPortal = false,
  portalRootId = DEFAULT_PORTAL_ROOT_ID,
}) => {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const unregisterOwnedNodeRef = useRef(null);
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });
  const [menuWidth, setMenuWidth] = useState(undefined);
  const isFloating = Boolean(anchorRef);
  const anchoredPortalContext = useContext(AnchoredPortalContext);
  const portalRoot = useMemo(
    () => (renderInPortal && isFloating ? resolvePortalRoot(portalRootId) : null),
    [isFloating, portalRootId, renderInPortal],
  );
  const offsetX = offset?.x ?? DEFAULT_OFFSET.x;
  const offsetY = offset?.y ?? DEFAULT_OFFSET.y;

  useScrollLock(listRef, { enabled: lockScroll });

  const setRootRef = useCallback((node) => {
    if (unregisterOwnedNodeRef.current) {
      unregisterOwnedNodeRef.current();
      unregisterOwnedNodeRef.current = null;
    }

    rootRef.current = node;
    assignRef(dropdownRef, node);

    if (renderInPortal && node && anchoredPortalContext?.registerOwnedNode) {
      unregisterOwnedNodeRef.current = anchoredPortalContext.registerOwnedNode(node);
    }
  }, [anchoredPortalContext, dropdownRef, renderInPortal]);

  useEffect(() => () => {
    if (unregisterOwnedNodeRef.current) {
      unregisterOwnedNodeRef.current();
      unregisterOwnedNodeRef.current = null;
    }
  }, []);

  const resolvePositionContainer = useCallback(() => {
    if (strategy === "fixed") {
      return typeof document !== "undefined" ? document.documentElement : null;
    }

    const root = rootRef.current;
    if (!root) {
      return null;
    }

    return root.offsetParent || root.parentElement || null;
  }, [strategy]);

  const resolveScrollContainers = useCallback(() => {
    if (!isFloating) {
      return [];
    }

    if (strategy === "fixed") {
      return typeof window !== "undefined" ? [window] : [];
    }

    const container = resolvePositionContainer();
    return container ? [container] : [];
  }, [isFloating, resolvePositionContainer, strategy]);

  const viewportTick = useViewportChangeTick(resolveScrollContainers, [
    className,
    flip,
    gap,
    isFloating,
    items.length,
    matchTriggerWidth,
    menuRole,
    offsetX,
    offsetY,
    placement,
    strategy,
  ]);

  useLayoutEffect(() => {
    if (!isFloating) {
      if (menuWidth !== undefined) {
        setMenuWidth(undefined);
      }
      return;
    }

    const dropdown = rootRef.current;
    const anchor = resolveRefElement(anchorRef);
    const container = resolvePositionContainer();

    if (!dropdown || !anchor || !container) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const nextWidth = matchTriggerWidth ? Math.round(anchorRect.width) : undefined;

    if (nextWidth !== menuWidth) {
      setMenuWidth(nextWidth);
    }

    const relativeAnchor = strategy === "fixed"
      ? {
        top: anchorRect.top,
        left: anchorRect.left,
        width: anchorRect.width,
        height: anchorRect.height,
      }
      : {
        top: anchorRect.top - containerRect.top + (container.scrollTop || 0),
        left: anchorRect.left - containerRect.left + (container.scrollLeft || 0),
        width: anchorRect.width,
        height: anchorRect.height,
      };

    const nextCoords = calculateFloatingPosition({
      container,
      dropdown,
      anchor: relativeAnchor,
      gap,
      offset: { x: offsetX, y: offsetY },
      placement,
      flip,
      strategy,
      dropdownSize: nextWidth ? { width: nextWidth } : undefined,
    });

    if (nextCoords.top !== menuCoords.top || nextCoords.left !== menuCoords.left) {
      setMenuCoords({
        top: nextCoords.top,
        left: nextCoords.left,
      });
    }
  }, [
    anchorRef,
    flip,
    gap,
    isFloating,
    matchTriggerWidth,
    menuCoords.left,
    menuCoords.top,
    menuWidth,
    offsetX,
    offsetY,
    placement,
    resolvePositionContainer,
    strategy,
    viewportTick,
  ]);

  const renderDropdownItem = (item, index) => {
    if (typeof renderItem === "function") {
      return renderItem(item, index);
    }

    const itemId = getItemId(item, index);
    const itemLabel = getItemLabel(item, index);

    return (
      <DropDownItem
        key={itemId}
        className={itemClassName}
        role={menuRole === "listbox" ? "option" : "menuitem"}
        onClick={() => onItemSelect?.(item, index)}
      >
        {itemLabel}
      </DropDownItem>
    );
  };

  const rootClassName = classNames(
    "dropdown",
    {
      "dropdown--floating": isFloating,
      "dropdown--inline": !isFloating,
      "dropdown--fixed": isFloating && strategy === "fixed",
      "dropdown--match-trigger-width": isFloating && matchTriggerWidth,
    },
    className,
  );

  const rootStyle = isFloating
    ? {
      top: menuCoords.top,
      left: menuCoords.left,
      width: menuWidth,
    }
    : undefined;

  const dropdownNode = (
    <div className={rootClassName} ref={setRootRef} style={rootStyle}>
      <DropDownMenu menuRef={listRef} role={menuRole}>
        {items.length > 0 ? (
          items.map((item, index) => renderDropdownItem(item, index))
        ) : (
          <p className="dropdown-no-items">{noItemsMessage}</p>
        )}
      </DropDownMenu>
    </div>
  );

  if (portalRoot) {
    return createPortal(dropdownNode, portalRoot);
  }

  return dropdownNode;
};

DropDown.propTypes = {
  dropdownRef: elementRefPropType,
  anchorRef: elementRefPropType,
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func,
  onItemSelect: PropTypes.func,
  getItemId: PropTypes.func,
  getItemLabel: PropTypes.func,
  menuRole: PropTypes.oneOf(["menu", "listbox"]),
  itemClassName: PropTypes.string,
  noItemsMessage: PropTypes.string,
  className: PropTypes.string,
  lockScroll: PropTypes.bool,
  placement: PropTypes.oneOf([
    "top-start",
    "top-center",
    "top-end",
    "right-start",
    "right-center",
    "right-end",
    "bottom-start",
    "bottom-center",
    "bottom-end",
    "left-start",
    "left-center",
    "left-end",
  ]),
  offset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  gap: PropTypes.number,
  flip: PropTypes.bool,
  strategy: PropTypes.oneOf(["absolute", "fixed"]),
  matchTriggerWidth: PropTypes.bool,
  renderInPortal: PropTypes.bool,
  portalRootId: PropTypes.string,
};

export default DropDown;
